import { Client } from '@stomp/stompjs';
import { ArrowLeft, Send, Mail } from 'lucide-react';
import { FormEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { 
  Conversation, 
  getApiMessages, 
  getApiMessagesConversations, 
  getApiMessagesUnreadCount,
  postApiMessagesMarkRead,
  Message, 
  postApiMessages 
} from '../../api/messages-client';
import LoadingSpinner from '../LoadingSpinner';
import { PageContainer } from './LandingPage';
import { useAuth } from '../../AuthContext';
import MessageListingPreview from '../MessageListingPreview';
import { useUnreadMessages } from '../../UnreadMessagesContext';
import MainHeader from '../MainHeader';


interface ExtendedMessage extends Message {
  pending?: boolean;
  error?: boolean;
}

const formatMessageDate = (date: Date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  }
};

interface MessagesByDate {
  [key: string]: ExtendedMessage[];
}

const groupMessagesByDate = (messages: ExtendedMessage[]): MessagesByDate => {
  return messages.reduce((groups: MessagesByDate, message) => {
    const date = new Date(message.sent_at || '').toDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});
};

const MessageGroup = ({ messages, userId, conversations }: { 
  messages: ExtendedMessage[], 
  userId: string,
  conversations: Conversation[]
}) => {
  const messagesByDate = groupMessagesByDate(messages);

  // Sort dates in reverse chronological order
  const sortedDates = Object.keys(messagesByDate).sort((a, b) =>
    new Date(b).getTime() - new Date(a).getTime()
  );

  // Helper function to get sender name
  const getSenderName = (senderId: string | undefined) => {
    if (senderId === userId) return 'You';
    return conversations.find(c => c.account?.id === senderId)?.account?.name || 'Unknown';
  };

  // Helper function to determine if timestamp should be shown
  // Timestamps are shown if the message is the last in a cluster
  // A new cluster starts if:
  // - Next message is from a different sender
  // - Next message is sent more than 5 minutes later
  const shouldShowTimestamp = (currentMsg: ExtendedMessage, nextMsg: ExtendedMessage | null) => {
    if (!nextMsg) return true; // Always show timestamp for the last message in a group
    
    // Different sender = new cluster = show timestamp
    if (currentMsg.sender_id !== nextMsg.sender_id) return true;
    
    // Check if time difference is greater than 5 minutes (300000ms)
    const currentTime = new Date(currentMsg.sent_at || '').getTime();
    const nextTime = new Date(nextMsg.sent_at || '').getTime();
    return (nextTime - currentTime) > 300000;
  };

  return (
    <div className="flex flex-col-reverse space-y-reverse space-y-4">
      {sortedDates.map(dateStr => (
        <div key={dateStr} className="space-y-1">
          <div className="flex justify-center">
            <div className="text-sm px-2.5 py-1 rounded-full bg-[#24ccf2]/10 text-[#2476f2]">
              {formatMessageDate(new Date(dateStr))}
            </div>
          </div>

          {(() => {
            // Sort messages within each day in chronological order
            const dayMessages = [...messagesByDate[dateStr]].reverse();
            
            return dayMessages.map((message, index) => {
              const nextMessage = index < dayMessages.length - 1 ? dayMessages[index + 1] : null;
              const showTimestamp = shouldShowTimestamp(message, nextMessage);
              
              return (
                <div
                  key={message.id}
                  className={`flex ${message.sender_id === userId ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[70%] ${message.error ? 'opacity-50' : ''} ${message.sender_id === userId ? 'flex flex-col items-end' : ''}`}>
                    {message.listing && (
                      <MessageListingPreview 
                        listing={message.listing}
                        senderName={getSenderName(message.sender_id)}
                      />
                    )}
                    
                    <div
                      className={`relative px-4 py-2 rounded-xl shadow-sm text-sm
                        ${message.sender_id === userId
                          ? message.pending 
                            ? 'bg-gradient-to-r from-[#24ccf2]/60 to-[#2476f2]/60 text-white' // Less saturated gradient for pending
                            : 'bg-gradient-to-r from-[#24ccf2] to-[#2476f2] text-white'
                          : 'bg-white border border-gray-200'
                        }`}
                    >
                      <div className="whitespace-pre-wrap break-words">
                        {message.content}
                        {message.error && (
                          <span className="ml-2 text-red-500">⚠️</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Only show timestamp for the last message in a cluster */}
                    {showTimestamp && (
                      <div className={`text-xs mt-1 text-gray-500 w-full ${message.sender_id === userId ? 'text-right' : ''}`}>
                        {new Date(message.sent_at || '').toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {message.pending && <span className="ml-1">• Sending</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      ))}
    </div>
  );
};

export const Chat = () => {
  const { user, token } = useAuth();
  const { refreshUnreadCount } = useUnreadMessages();

  const [client, setClient] = useState<Client | null>(null);

  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [conversationsCursor, setConversationsCursor] = useState<string>();
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [completedFetchingConversations, setCompletedFetchingConversations] = useState(false);
  const [showConversationList, setShowConversationList] = useState(true);
  
  const [messagesCursor, setMessagesCursor] = useState<string>();
  const [messages, setMessages] = useState<ExtendedMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [completedFetchingMessages, setCompletedFetchingMessages] = useState(false);
  const [hasPendingMessages, setHasPendingMessages] = useState(false);

  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

  // Single ref to track current conversation (UUID of the other participant in the conversation)
  const currentConversationRef = useRef<string | null>(null);

  const messageContainerRef = useRef<HTMLDivElement>(null);
  const conversationContainerRef = useRef<HTMLDivElement>(null);

  const decodeHTML = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  const fetchUnreadCount = useCallback(async () => {
    try {
      await refreshUnreadCount(true);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [refreshUnreadCount]);

  useEffect(() => {
    fetchUnreadCount();
  }, []);

  useEffect(() => {
    const wsClient = new Client({
      brokerURL: 'ws://localhost:8080/chat',
      connectHeaders: { Authorization: `Bearer ${token}` },
      onConnect: () => {
        wsClient.subscribe('/user/queue/messages', (message) => {
          const receivedMessage = JSON.parse(message.body);
          handleNewMessage(receivedMessage);
          // When a new message is received, update the unread count
          fetchUnreadCount();
        });
      },
    });

    wsClient.activate();
    setClient(wsClient);

    return () => {
      wsClient.deactivate();
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingMessages) {
        e.preventDefault();
        return (e.returnValue = 'You have unsent messages. Are you sure you want to leave?');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingMessages]);

  const handleNewMessage = useCallback((newMessage: Message) => {
    // Only process message if it's for the currently selected conversation
    // Ignore the rest. It may have came out of order and we don't cache conversation messages.
    if (currentConversationRef.current !== newMessage.sender_id &&
      currentConversationRef.current !== newMessage.receiver_id) {
      return;
    }

    const messageWithDecodedHTML = {
      ...newMessage,
      content: decodeHTML(newMessage.content || ''),
    };

    setMessages(prev => {
      return [messageWithDecodedHTML, ...prev];
    });
  }, []);

  const loadMoreConversations = useCallback(async () => {
    if (completedFetchingConversations || loadingConversations) return;

    setLoadingConversations(true);
    try {
      const data = await getApiMessagesConversations({
        cursor: conversationsCursor,
        pageSize: 40
      });

      if (data.length > 0) {
        const decodedData = data.map(conv => ({
          ...conv,
          messages: conv.messages?.map(msg => ({
            ...msg,
            content: decodeHTML(msg.content || '')
          }))
        }));
        setConversations(prev => [...prev, ...decodedData]);
        setConversationsCursor(data[data.length - 1]?.messages?.[0]?.sent_at);
      } else {
        setCompletedFetchingConversations(true);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setCompletedFetchingConversations(true);
    } finally {
      setLoadingConversations(false);
    }
  }, [conversationsCursor, completedFetchingConversations, loadingConversations]);

  const loadMoreMessages = useCallback(async () => {
    if (!currentConversationRef.current || loadingMessages || completedFetchingMessages) return;

    setLoadingMessages(true);
    setShowLoadingSpinner(true);

    const requestedConversation = currentConversationRef.current;

    try {
      const data = await getApiMessages({
        cursor: messagesCursor,
        pageSize: 40,
        otherUserId: requestedConversation,
        markAsRead: true // Mark messages as read when loading them
      });

      // Only update if this is still the current conversation
      if (currentConversationRef.current === requestedConversation) {
        if (data.length > 0) {
          const decodedData = data.map(msg => ({
            ...msg,
            content: decodeHTML(msg.content || ''),
            sender_id: msg.sender_id === requestedConversation ? requestedConversation : user?.userId
          }));

          setMessages(prev => [...prev, ...decodedData]);
          setMessagesCursor(data[data.length - 1]?.sent_at);
        } else {
          setCompletedFetchingMessages(true);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (currentConversationRef.current === requestedConversation) {
        setCompletedFetchingMessages(true);
      }
    } finally {
      if (currentConversationRef.current === requestedConversation) {
        setLoadingMessages(false);
        setShowLoadingSpinner(false);
      }
    }
  }, [messagesCursor, loadingMessages, completedFetchingMessages]);

  const handleConversationSelect = useCallback(async (conversationId: string) => {
    // Update refs and state
    currentConversationRef.current = conversationId;
    setSelectedConversation(conversationId);
    setMessages([]);
    setMessagesCursor(undefined);
    setShowLoadingSpinner(true);
    setCompletedFetchingMessages(false);
    setShowConversationList(false);

    try {
      // Update the local conversation list to remove unread indicator
      setConversations(prev => 
        prev.map(conv => 
          conv.account?.id === conversationId 
            ? { ...conv, has_unread_messages: false } 
            : conv
        )
      );

      const data = await getApiMessages({
        cursor: undefined,
        pageSize: 40,
        otherUserId: conversationId,
        markAsRead: true
      });

      if (currentConversationRef.current === conversationId) {
        if (data.length > 0) {
          const decodedData = data.map(msg => ({
            ...msg,
            content: decodeHTML(msg.content || '')
            // Removed the sender_id transformation
          }));

          setMessages(decodedData);
          setMessagesCursor(data[data.length - 1]?.sent_at);
        }
      }

      fetchUnreadCount();
    } catch (error) {
      console.error('Error loading initial messages:', error);
    } finally {
      if (currentConversationRef.current === conversationId) {
        setShowLoadingSpinner(false);
      }
    }
  }, []);

  const sendMessage = useCallback(async (content: string) => {
    if (!selectedConversation) return;

    const tempId = Date.now().toString();
    const optimisticMessage: ExtendedMessage = {
      id: tempId,
      sender_id: user?.userId,
      receiver_id: selectedConversation,
      content,
      sent_at: new Date().toISOString(),
      pending: true,
      is_read: false // sender's message is not read by receiver yet
    };

    setMessages(prev => [optimisticMessage, ...prev]);
    setHasPendingMessages(true);

    try {
      const response = await postApiMessages({
        requestBody: {
          receiver_id: selectedConversation,
          content
        }
      });

      // Update the message to remove pending state after successful sending
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, pending: false, id: response.id || tempId }
            : msg
        )
      );
    } catch (error) {
      console.error('Error sending  message:', error);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === tempId
            ? { ...msg, error: true, pending: false }
            : msg
        )
      );
    } finally {
      setHasPendingMessages(false);
    }
  }, [selectedConversation]);

  // Initial load effect
  useEffect(() => {
    loadMoreConversations();
  }, []);

  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      if (target === messageContainerRef.current) {
        // For a flex-col-reverse layout, we need to check when we're at the visual top
        // (which is actually the historical top of the messages)
        const scrolledToTop =
          Math.abs(target.scrollHeight + target.scrollTop - target.clientHeight) < 20;

        if (scrolledToTop && !loadingMessages && !completedFetchingMessages) {
          loadMoreMessages();
        }
      } else if (target === conversationContainerRef.current) {
        // Regular top loading for conversations
        if (target.scrollTop === 0) {
          loadMoreConversations();
        }
      }
    };

    messageContainerRef.current?.addEventListener('scroll', handleScroll);
    conversationContainerRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      messageContainerRef.current?.removeEventListener('scroll', handleScroll);
      conversationContainerRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreMessages, loadMoreConversations, loadingMessages, completedFetchingMessages]);

  if (!user?.userId) {
    return (
      <h3>No valid user ID found. Perhaps you have disabled local storage/cookies.</h3>
    ); // TODO: True visual error state
  }

  return (
    <div className="h-screen flex flex-col">
      <MainHeader />
      <div className="flex-1 min-h-0"> {/* Contains everything except header */}
        <PageContainer className="h-full">
          <div className="h-full flex">
            {/* Conversations Sidebar */}
            <div className={`
              w-full sm:block sm:w-80 sm:flex-none 
              sm:border-r border-gray-200 bg-white overflow-hidden flex flex-col
            `}>
              <div
                ref={conversationContainerRef}
                className="flex-1 overflow-y-auto"
              >
                {conversations.map((conv) => (
                  <div
                    key={conv.account?.id}
                    className={`group relative p-4 cursor-pointer transition-all duration-200 hover:bg-gradient-to-br from-[#24ccf2]/5 via-[#2476f2]/5 to-purple-500/5  ${
                      selectedConversation === conv.account?.id && !showConversationList
                        ? 'bg-gradient-to-br from-[#24ccf2]/10 via-[#2476f2]/10 to-purple-500/10'
                        : ''
                    }`}
                    onClick={() => handleConversationSelect(conv.account?.id || '')}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="relative group w-10 h-10">
                        {conv.account?.profilePicture ? (
                          <img
                            src={conv.account.profilePicture}
                            alt={conv.account.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-lg font-medium text-gray-600">
                              {conv.account?.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                        {/* Unread indicator dot */}
                        {conv.has_unread_messages && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 bg-[#24a1f2] rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-gray-900 ${conv.has_unread_messages ? 'font-bold' : ''}`}>
                          {conv.account?.name}
                        </div>
                        <div className={`text-sm ${conv.has_unread_messages ? 'text-gray-900 font-medium' : 'text-gray-500'} truncate`}>
                          {conv.messages?.[0]?.content}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {loadingConversations && (
                  <div className="flex justify-center p-4">
                    <LoadingSpinner />
                  </div>
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className={`${!showConversationList ? 'translate-x-0' : 'translate-x-full'} 
              transition-transform duration-200
              fixed sm:static inset-0 sm:inset-auto
              sm:translate-x-0 sm:flex-1
              flex flex-col bg-white
              z-50
              sm:z-20
            `}>
              {selectedConversation ? (
                <div className="h-full flex flex-col"> {/* Important: This ensures proper flex layout */}
                  {/* Chat Header */}
                  <div className="flex-none p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setShowConversationList(true)}
                        className="sm:hidden flex-shrink-0 mr-4"
                      >
                        <ArrowLeft className="h-6 w-6" />
                      </button>
                      <div className="relative group w-10 h-10">
                        {conversations.find(c => c.account?.id === selectedConversation)?.account?.profilePicture ? (
                          <img
                            src={conversations.find(c => c.account?.id === selectedConversation)?.account?.profilePicture}
                            alt={conversations.find(c => c.account?.id === selectedConversation)?.account?.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-100 rounded-full">
                            <span className="text-lg font-medium text-gray-600">
                              {conversations.find(c => c.account?.id === selectedConversation)?.account?.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {conversations.find(c => c.account?.id === selectedConversation)?.account?.name}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Messages Container */}
                  <div
                    ref={messageContainerRef}
                    className="flex-1 overflow-y-auto flex flex-col-reverse"
                  >
                    <div className="flex-1">
                      <div className="p-4">
                        {showLoadingSpinner && (
                          <div className="flex justify-center py-2">
                            <LoadingSpinner />
                          </div>
                        )}
                        <MessageGroup messages={messages} userId={user?.userId} conversations={conversations} />
                      </div>
                    </div>
                  </div>

                  {/* Message Input - Now properly fixed at bottom */}
                  <div className="flex-none p-4 bg-white border-t border-gray-200">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const input = e.currentTarget.elements.namedItem('message') as HTMLTextAreaElement;
                        if (input.value.trim()) {
                          sendMessage(input.value);
                          input.value = '';
                          input.style.height = '44px';
                        }
                      }}
                    >
                      <div className="relative flex-grow overflow-hidden border border-gray-200 rounded-lg focus-within:ring-2 focus-within:ring-[#24ccf2] bg-white/90">
                        <div className="flex items-stretch min-h-[44px]">
                          <textarea
                            name="message"
                            rows={1}
                            className="block w-full px-4 py-2.5 text-sm bg-transparent outline-none resize-none"
                            placeholder="Type a message..."
                            onInput={(e: FormEvent<HTMLTextAreaElement>) => {
                              const textarea = e.currentTarget;
                              const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
                              const maxRows = 4;
                              const maxHeight = lineHeight * maxRows;

                              textarea.style.height = '44px';

                              if (textarea.scrollHeight <= maxHeight) {
                                textarea.style.height = `${textarea.scrollHeight}px`;
                                textarea.style.overflowY = 'hidden';
                              } else {
                                textarea.style.height = `${maxHeight}px`;
                                textarea.style.overflowY = 'auto';
                              }
                            }}
                            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                              if (!isMobileDevice() && e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                const content = e.currentTarget.value.trim();
                                if (content) {
                                  sendMessage(content);
                                  e.currentTarget.value = '';
                                  e.currentTarget.style.height = '44px';
                                }
                              }
                            }}
                          />
                          <button
                            type="submit"
                            className="flex-none w-14 flex items-center justify-center text-white bg-gradient-to-r from-[#24ccf2] to-[#2476f2] hover:opacity-90 transition-opacity"
                          >
                            <Send className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <h3 className="text-2xl md:text-3xl font-semibold text-center mb-3 bg-gradient-to-r from-[#24ccf2] to-[#2476f2] bg-clip-text text-transparent">
                      Select a conversation
                    </h3>

                    <p className="text-gray-600 text-center max-w-md mb-8">
                      Choose a conversation from the sidebar to start chatting!
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </PageContainer>
      </div>
    </div>
  );
};

export default Chat;